import type {
  FreeformCell,
  FreeformChartConfig,
  FreeformChartData,
  FreeformChartType,
  PieSlice,
  SeriesDef,
} from './freeform-chart-artifact.type';

/** Reserved x-axis dataKey — no series column name can collide with it. */
const X_KEY = '__x';

const FREEFORM_CHART_TYPES: FreeformChartType[] = ['line', 'area', 'bar', 'stacked_bar', 'pie', 'table'];

/** Widest exact tooltip value before it reads as overflow. */
const MAX_DATA_LABEL_CHARS = 12;

/** Widest exact on-bar label — past "999,999" grouped bars collide, go compact. */
const MAX_BAR_LABEL_CHARS = 7;

/** Widest total the donut hole fits at its 22px font. */
const MAX_DONUT_TOTAL_CHARS = 9;

/** Widest center caption the donut hole fits at its 11px font. */
const MAX_DONUT_CAPTION_CHARS = 22;

/** Longer than this, horizontal ticks collide (recharts drops some) — angle them. */
const ANGLED_TICK_TRIGGER_CHARS = 12;

/** Widest angled tick before its diagonal outgrows the reserved axis band. */
const MAX_ANGLED_TICK_CHARS = 22;

/** Longest axis title — the rotated y title must fit the chart's 280px height. */
const MAX_AXIS_TITLE_CHARS = 42;

/**
 * Defensively narrows a raw artifact `data` payload to the columnar
 * `{columns, rows}` shape. The export route decodes arbitrary URL-hash
 * payloads, so malformed input must degrade to null (rendered as a
 * placeholder), never throw.
 *
 * @param {Record<string, unknown> | undefined} data - Raw artifact data payload.
 * @returns {FreeformChartData | null} The narrowed payload, or null when malformed or empty.
 */
function parseFreeformData(data: Record<string, unknown> | undefined): FreeformChartData | null {
  if (!data || typeof data !== 'object') return null;
  const { columns, rows } = data as { columns?: unknown; rows?: unknown };
  if (!Array.isArray(columns) || columns.length === 0 || !columns.every((col) => typeof col === 'string')) {
    return null;
  }
  if (!Array.isArray(rows) || rows.length === 0 || !rows.every((row) => Array.isArray(row))) {
    return null;
  }
  return {
    columns: columns as string[],
    rows: (rows as unknown[][]).map((row) => row.map(normalizeCell)),
  };
}

/**
 * Narrows a raw artifact `config` payload to the freeform chart presentation
 * config, defaulting missing text fields to empty strings.
 *
 * @param {Record<string, unknown> | undefined} config - Raw artifact config payload.
 * @returns {FreeformChartConfig | null} The parsed config, or null when chart_type is missing/unknown.
 */
function parseFreeformConfig(config: Record<string, unknown> | undefined): FreeformChartConfig | null {
  if (!config || typeof config !== 'object') return null;
  const chartType = config.chart_type;
  if (typeof chartType !== 'string' || !FREEFORM_CHART_TYPES.includes(chartType as FreeformChartType)) {
    return null;
  }
  return {
    chartType: chartType as FreeformChartType,
    subtitle: typeof config.subtitle === 'string' ? config.subtitle : '',
    xAxisLabel: typeof config.x_axis_label === 'string' ? config.x_axis_label : '',
    yAxisLabel: typeof config.y_axis_label === 'string' ? config.y_axis_label : '',
  };
}

/**
 * Normalizes an unknown cell to the FreeformCell union: strings/numbers pass
 * through, null/undefined become null, anything else is stringified.
 *
 * @param {unknown} cell - Raw cell value from the decoded payload.
 * @returns {FreeformCell} The normalized cell.
 */
function normalizeCell(cell: unknown): FreeformCell {
  if (cell == null) return null;
  if (typeof cell === 'string' || typeof cell === 'number') return cell;
  return String(cell);
}

/**
 * Coerces a series cell to a finite number. Numeric strings are accepted
 * (thousands separators and common unit prefixes/suffixes stripped) so
 * defensive rendering matches the BE tool's coercion.
 *
 * @param {unknown} value - Raw series cell value.
 * @returns {number | null} The finite number, or null when not numeric.
 */
function coerceSeriesValue(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[,%$\s]/g, '');
    if (cleaned === '') return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Builds the series definitions for a columnar payload: one entry per column
 * after the first, each with a positional recharts dataKey (`s1`, `s2`, ...)
 * and the column name as its display label. Positional keys keep adversarial
 * column names display-only — recharts resolves dataKeys as lodash deep paths
 * (dots would traverse), a column named `__x` would collide with the reserved
 * x key, and duplicate names would collapse into one series.
 *
 * @param {string[]} columns - Column headers; first is the x-axis.
 * @returns {SeriesDef[]} Positional series definitions in column order.
 */
function buildSeries(columns: string[]): SeriesDef[] {
  return columns.slice(1).map((name, index) => ({ key: `s${index + 1}`, name }));
}

/**
 * Maps the columnar payload to recharts row objects: `columns[0]` becomes the
 * x value (under the reserved X_KEY) and each remaining column becomes one
 * series keyed positionally (see `buildSeries`), with non-numeric cells
 * nulled to gaps.
 *
 * @param {string[]} columns - Column headers; first is the x-axis.
 * @param {FreeformCell[][]} rows - Data rows aligned to columns.
 * @returns {Record<string, FreeformCell>[]} One recharts row object per input row.
 */
function buildChartRows(columns: string[], rows: FreeformCell[][]): Record<string, FreeformCell>[] {
  return rows.map((row) => {
    const chartRow: Record<string, FreeformCell> = { [X_KEY]: row[0] ?? null };
    for (let colIdx = 1; colIdx < columns.length; colIdx += 1) {
      chartRow[`s${colIdx}`] = coerceSeriesValue(row[colIdx]);
    }
    return chartRow;
  });
}

/**
 * Builds donut slices from pie rows (`[label, value]`), skipping rows whose
 * value is missing, non-numeric, or not positive. Slice percents are left to
 * recharts, which computes them over the currently visible slices.
 *
 * @param {FreeformCell[][]} rows - Pie data rows: `[label, value]` pairs.
 * @returns {PieSlice[]} Renderable slices; empty when nothing renders.
 */
function buildPieSlices(rows: FreeformCell[][]): PieSlice[] {
  return rows.flatMap((row) => {
    const value = coerceSeriesValue(row[1]);
    if (value == null || value <= 0) return [];
    return [{ label: String(row[0] ?? ''), value }];
  });
}

/**
 * Sums the values of the given slices (the donut's center total).
 *
 * @param {PieSlice[]} slices - Slices to sum.
 * @returns {number} The total value, 0 for an empty array.
 */
function sumPieTotal(slices: PieSlice[]): number {
  return slices.reduce((sum, slice) => sum + slice.value, 0);
}

/**
 * Formats a summed total for display (the donut center and tooltip totals):
 * thousands-separated, with at most one fraction digit so "100000" reads as
 * "100,000" and float-addition artifacts like 23.599999999999998 read as
 * "23.6".
 *
 * @param {number} total - The summed total.
 * @returns {string} The locale-formatted total.
 */
function formatTotal(total: number): string {
  // One fraction digit erases sub-1 data — a 0.04 churn rate must not read
  // "0" — so small magnitudes keep significant digits (matching the axis
  // ticks) while larger ones keep the artifact-hiding one-decimal rounding.
  const text =
    total !== 0 && Math.abs(total) < 1
      ? total.toLocaleString(undefined, { maximumSignificantDigits: 4 })
      : total.toLocaleString(undefined, { maximumFractionDigits: 1 });
  // exact minus-zero still formats with a bare sign
  return text === '-0' ? '0' : text;
}

/**
 * Formats a numeric axis tick. Large magnitudes are compacted (1000000 -> "1M",
 * 750000 -> "750K") so they can't overrun the axis band and collide with the
 * rotated axis title. Smaller values are formatted by significant digits, not
 * decimal places, so ticks stay readable at any scale — a fixed decimal cap
 * would flatten a whole small-value axis (rates, ratios) to "0".
 *
 * @param {number} value - The raw tick value from recharts.
 * @returns {string} The formatted tick label.
 */
function formatAxisTick(value: number): string {
  if (!Number.isFinite(value)) return '';
  // Compact notation tops out at "T", so astronomic values expand back into
  // long strings ("1,000,000,000T") — switch to scientific before that.
  if (Math.abs(value) >= 1e15) {
    return value.toLocaleString(undefined, { notation: 'scientific', maximumFractionDigits: 1 });
  }
  if (Math.abs(value) >= 1000) {
    return value.toLocaleString(undefined, { notation: 'compact', maximumFractionDigits: 1 });
  }
  // The tiny-value mirror of the 1e15 case: significant-digit form expands
  // 2.5e-10 to "0.00000000025", which overruns the axis band and clips to
  // garbage. Above 1e-4 the plain form is at most 9 chars, which fits.
  if (value !== 0 && Math.abs(value) < 1e-4) {
    return value.toLocaleString(undefined, {
      notation: 'scientific',
      maximumSignificantDigits: 4,
    });
  }
  return value.toLocaleString(undefined, { maximumSignificantDigits: 4 });
}

/**
 * Formats a tooltip value. Prefers the exact thousands-separated form, but
 * falls back to the axis's compact form for astronomic magnitudes where the
 * exact form stops being readable.
 *
 * @param {number} value - The value to label.
 * @returns {string} The label text.
 */
function formatDataLabel(value: number): string {
  const exact = formatTotal(value);
  return exact.length > MAX_DATA_LABEL_CHARS ? formatAxisTick(value) : exact;
}

/**
 * Decides whether the x-axis needs angled category ticks. Horizontal labels
 * are cleaner, so stay flat while every label is short enough to fit its
 * category slot; past that, recharts starts silently dropping colliding
 * labels, which is worse than the angle.
 *
 * @param {string[]} labels - The category (x-axis) labels, in render order.
 * @returns {boolean} True when the axis should render angled ticks.
 */
function needsAngledTicks(labels: string[]): boolean {
  // trim so padding whitespace (invisible on the axis) can't force angling
  return labels.some((label) => label.trim().length > ANGLED_TICK_TRIGGER_CHARS);
}

/**
 * Clips text to a code-point budget with an ellipsis. Counting and cutting by
 * code point drops an emoji whole instead of splitting it into a broken glyph.
 *
 * @param {unknown} text - Raw text value.
 * @param {number} maxCodePoints - Budget including the ellipsis.
 * @returns {string} The text, at most maxCodePoints code points long.
 */
function clipWithEllipsis(text: unknown, maxCodePoints: number): string {
  const codePoints = [...String(text ?? '')];
  if (codePoints.length <= maxCodePoints) return codePoints.join('');
  return `${codePoints.slice(0, maxCodePoints - 1).join('')}…`;
}

/**
 * Truncates a category label for an angled tick, with an ellipsis. The full
 * label stays available on hover via the chart tooltip.
 *
 * @param {unknown} label - Raw category value from the axis payload.
 * @returns {string} The tick text, at most MAX_ANGLED_TICK_CHARS long.
 */
function truncateAxisLabel(label: unknown): string {
  return clipWithEllipsis(label, MAX_ANGLED_TICK_CHARS);
}

/**
 * Truncates an axis title (the x/y caption). The rotated y title only has the
 * chart's height to live in, so anything longer overflows the card; the full
 * title stays available on hover via an SVG <title>.
 *
 * @param {unknown} title - The axis caption from the chart config.
 * @returns {string} The title, at most MAX_AXIS_TITLE_CHARS long.
 */
function truncateAxisTitle(title: unknown): string {
  return clipWithEllipsis(title, MAX_AXIS_TITLE_CHARS);
}

/**
 * Truncates the donut's center caption to fit the hole; the full caption
 * stays available on hover via an SVG <title>.
 *
 * @param {unknown} caption - The center caption under the donut total.
 * @returns {string} The caption, at most MAX_DONUT_CAPTION_CHARS long.
 */
function truncateDonutCaption(caption: unknown): string {
  return clipWithEllipsis(caption, MAX_DONUT_CAPTION_CHARS);
}

/**
 * Formats the donut's center total. Same exact-then-compact strategy as the
 * bar labels, but with the hole's tighter character budget — a large total
 * like 13,962,500,000 renders as "14B" instead of clipping on the ring.
 *
 * @param {number} total - The summed total of the visible slices.
 * @returns {string} The center-total text.
 */
function formatDonutTotal(total: number): string {
  const exact = formatTotal(total);
  return exact.length > MAX_DONUT_TOTAL_CHARS ? formatAxisTick(total) : exact;
}

/**
 * Formats a bar's value label: exact while it fits the bar's slot, compact
 * ("145M") from millions up. Tooltips keep the exact form, so no precision is
 * lost — only the drawn-on-chart text shortens. Non-numeric cells (nulls,
 * gaps) render nothing rather than "null".
 *
 * @param {unknown} value - Raw value handed over by the recharts LabelList.
 * @returns {string} The formatted label, or an empty string.
 */
function formatBarLabel(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const exact = formatTotal(value);
  return exact.length > MAX_BAR_LABEL_CHARS ? formatAxisTick(value) : exact;
}

/**
 * Formats a donut slice's share as a whole-number percent. Slice labels are the
 * tightest text in the chart and a donut conveys proportion, not precision — the
 * exact value is one hover away. Rounding means shown labels can sum to 99 or
 * 101; sub-5% slices are unlabelled anyway, so none can round to "0%".
 *
 * @param {number} percent - The slice's share, as a 0-1 fraction.
 * @returns {string} The percent label, e.g. "49%".
 */
function formatSlicePercent(percent: number): string {
  return `${Math.round(percent * 100)}%`;
}

/**
 * Formats one tooltip row's value, matching the numbers drawn on the chart
 * itself. Gaps render as an em dash so a missing point is legible rather than
 * blank.
 *
 * @param {unknown} value - Raw value from the recharts tooltip payload.
 * @returns {string} The formatted value, or an em dash for a gap.
 */
function formatTooltipValue(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return formatDataLabel(value);
  return value == null ? '—' : String(value);
}

/**
 * Formats a table cell for display: null/undefined render as an em dash,
 * everything else verbatim.
 *
 * @param {unknown} value - Raw cell value.
 * @returns {string} The display string.
 */
function formatCellValue(value: unknown): string {
  if (value == null) return '—';
  return String(value);
}

export {
  X_KEY,
  buildChartRows,
  buildPieSlices,
  buildSeries,
  coerceSeriesValue,
  formatAxisTick,
  formatBarLabel,
  formatCellValue,
  formatDataLabel,
  formatDonutTotal,
  formatSlicePercent,
  formatTooltipValue,
  formatTotal,
  needsAngledTicks,
  truncateAxisLabel,
  truncateAxisTitle,
  truncateDonutCaption,
  parseFreeformConfig,
  parseFreeformData,
  sumPieTotal,
};
